import { useState, useEffect } from 'react';
import { 
  FiFileText, 
  FiUser, 
  FiClock, 
  FiCheckCircle, 
  FiChevronRight,
  FiArrowLeft,
  FiSend,
  FiSearch
} from 'react-icons/fi';
import WargaLayout from '../../components/WargaLayout';
import SuccessModal from '../../components/SuccessModal';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const SuratMobile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast, hideToast, success, error, warning } = useToast();
  const [jenisSurat, setJenisSurat] = useState([]);
  const [filteredJenisSurat, setFilteredJenisSurat] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJenis, setSelectedJenis] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingNik, setLoadingNik] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [step, setStep] = useState(1); // 1: pilih surat, 2: isi form

  useEffect(() => {
    fetchJenisSurat();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredJenisSurat(jenisSurat);
    } else {
      const filtered = jenisSurat.filter(surat =>
        surat.nama_surat?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        surat.deskripsi?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredJenisSurat(filtered);
    }
  }, [searchQuery, jenisSurat]);

  const fetchJenisSurat = async () => {
    try {
      setLoading(true);
      const response = await api.get('/warga/jenis-surat');
      if (response.data.success) {
        setJenisSurat(response.data.data);
        setFilteredJenisSurat(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching jenis surat:', err);
      error('Gagal memuat daftar jenis surat');
    } finally {
      setLoading(false);
    }
  };

  const fetchWargaByNik = async (nik) => {
    if (!nik || nik.length !== 16) return;

    try {
      setLoadingNik(true);
      const response = await api.get(`/warga/data-by-nik/${nik}`);
      
      if (response.data.success) {
        const wargaData = response.data.data;
        
        // Format tanggal lahir dari timestamp ke YYYY-MM-DD dan Indonesia
        let formattedTanggalLahir = '';
        let formattedTanggalLahirIndonesia = '';
        if (wargaData.tanggal_lahir) {
          const date = new Date(wargaData.tanggal_lahir);
          formattedTanggalLahir = date.toISOString().split('T')[0]; // 2017-01-06
          
          // Format Indonesia: 15 Januari 1990
          const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
                         'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
          const day = date.getDate();
          const month = months[date.getMonth()];
          const year = date.getFullYear();
          formattedTanggalLahirIndonesia = `${day} ${month} ${year}`;
          
          console.log('📅 Formatted tanggal_lahir:', formattedTanggalLahir);
          console.log('📅 Formatted tanggal_lahir Indonesia:', formattedTanggalLahirIndonesia);
        }

        const normalizeFieldName = (name) => {
          return name.toLowerCase()
            .replace(/\s+/g, '_')
            .replace(/[^a-z0-9_]/g, '');
        };

        const templateFields = selectedJenis?.fields || [];
        console.log('📋 Template fields:', templateFields);

        // Master mapping of warga data (all possible fields)
        const wargaFieldMapping = {
          'nik': wargaData.nik,
          'nama': wargaData.nama,
          'nama_lengkap': wargaData.nama,
          'tempat_lahir': wargaData.tempat_lahir,
          'tempatlahir': wargaData.tempat_lahir,
          'tanggal_lahir': formattedTanggalLahir,
          'tanggallahir': formattedTanggalLahir,
          'ttl': formattedTanggalLahir,
          // Special: Tempat, Tanggal Lahir (combined dengan format Indonesia)
          'tempat_tanggal_lahir': wargaData.tempat_lahir && formattedTanggalLahirIndonesia 
            ? `${wargaData.tempat_lahir}, ${formattedTanggalLahirIndonesia}` 
            : '',
          'tempattanggallahir': wargaData.tempat_lahir && formattedTanggalLahirIndonesia 
            ? `${wargaData.tempat_lahir}, ${formattedTanggalLahirIndonesia}` 
            : '',
          'jenis_kelamin': wargaData.jenis_kelamin,
          'jeniskelamin': wargaData.jenis_kelamin,
          'agama': wargaData.agama,
          'status_perkawinan': wargaData.status_perkawinan,
          'statusperkawinan': wargaData.status_perkawinan,
          'status_kawin': wargaData.status_perkawinan,
          'statuskawin': wargaData.status_perkawinan,
          'pekerjaan': wargaData.pekerjaan,
          'alamat': wargaData.alamat,
          'rt': wargaData.rt,
          'rw': wargaData.rw,
          'dusun': wargaData.dusun,
          'kelurahan': wargaData.kelurahan_desa,
          'kelurahan_desa': wargaData.kelurahan_desa,
          'kecamatan': wargaData.kecamatan,
          'kabupaten': wargaData.kabupaten_kota,
          'kabupaten_kota': wargaData.kabupaten_kota,
          'provinsi': wargaData.provinsi,
          'kewarganegaraan': wargaData.kewarganegaraan,
          'no_kk': wargaData.no_kk,
          'no_telepon': wargaData.no_telepon,
          'pendidikan': wargaData.pendidikan,
          'golongan_darah': wargaData.golongan_darah,
          'nama_kepala_keluarga': wargaData.nama_kepala_keluarga,
          'hubungan_keluarga': wargaData.hubungan_keluarga
        };

        // Update formData HANYA dengan field yang ada di template
        const newFormData = { ...formData };
        
        templateFields.forEach(field => {
          const normalizedFieldName = normalizeFieldName(field.name);
          console.log(`🔍 Checking template field: "${field.name}" → normalized: "${normalizedFieldName}"`);
          
          // Check if we have data for this field
          if (wargaFieldMapping[normalizedFieldName]) {
            newFormData[field.name] = wargaFieldMapping[normalizedFieldName];
            console.log(`✅ Autofilled: ${field.name} = ${wargaFieldMapping[normalizedFieldName]}`);
          } else {
            console.log(`⚠️ No mapping found for: ${normalizedFieldName}`);
          }
        });

        setFormData(newFormData);
        success('Data berhasil dimuat dari NIK');
      }
    } catch (err) {
      console.error('Error fetching warga data:', err);
      warning('Data warga tidak ditemukan. Silakan isi manual.');
    } finally {
      setLoadingNik(false);
    }
  };

  const handleSelectJenis = (jenis) => {
    setSelectedJenis(jenis);
    setFormData({});
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedJenis(null);
    setFormData({});
  };

  const handleInputChange = (e, fieldName) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    if (fieldName.toLowerCase().includes('nik') && value.length === 16) {
      fetchWargaByNik(value);
    }
  };



  const handleSubmit = async () => {
    // Validate required fields before submitting
    const requiredFields = selectedJenis.fields.filter(field => field.required);
    const emptyFields = requiredFields.filter(field => {
      const value = formData[field.name];
      // Check for null, undefined, empty string, or whitespace-only
      return !value || (typeof value === 'string' && value.trim() === '');
    });

    if (emptyFields.length > 0) {
      const missingLabels = emptyFields.map(f => f.label).join(', ');
      error(`Field wajib belum diisi: ${missingLabels}`);
      console.log('❌ Missing required fields (Submit):', emptyFields.map(f => f.label));
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/warga/surat', {
        jenis_surat_id: selectedJenis.id,
        data_surat: formData
      });

      if (response.data.success) {
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error('Error submitting surat:', err);
      error(err.response?.data?.message || 'Gagal mengajukan surat');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const isNikField = field.name.toLowerCase().includes('nik');
    
    return (
      <div key={field.name} className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {field.type === 'textarea' ? (
          <textarea
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(e, field.name)}
            placeholder={field.placeholder || `Masukkan ${field.label.toLowerCase()}`}
            required={field.required}
            rows={4}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all resize-none"
          />
        ) : field.type === 'select' ? (
          <select
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(e, field.name)}
            required={field.required}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all appearance-none bg-white"
          >
            <option value="">Pilih {field.label}</option>
            {field.options?.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <div className="relative">
            <input
              type={field.type || 'text'}
              value={formData[field.name] || ''}
              onChange={(e) => handleInputChange(e, field.name)}
              placeholder={field.placeholder || `Masukkan ${field.label.toLowerCase()}`}
              required={field.required}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            />
            {isNikField && loadingNik && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600"></div>
              </div>
            )}
          </div>
        )}
        
        {field.description && (
          <p className="text-xs text-slate-500">{field.description}</p>
        )}
      </div>
    );
  };

  if (loading && jenisSurat.length === 0) {
    return (
      <WargaLayout>
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700 mx-auto mb-4"></div>
            <p className="text-slate-600">Memuat data...</p>
          </div>
        </div>
      </WargaLayout>
    );
  }

  return (
    <WargaLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white px-4 pt-6 pb-8 rounded-b-3xl shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            {step === 2 && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <FiArrowLeft size={24} />
              </button>
            )}
            <h1 className="text-2xl font-bold">
              {step === 1 ? 'Pilih Jenis Surat' : selectedJenis?.nama_surat}
            </h1>
          </div>
          
          {step === 1 && (
            <div className="relative">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cari jenis surat..."
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 focus:bg-white/20 focus:outline-none transition-all"
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-4 -mt-4">
          {step === 1 ? (
            // Step 1: Pilih Jenis Surat (Card Grid)
            <div className="space-y-3">
              {filteredJenisSurat.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                  <FiFileText size={48} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Tidak ada jenis surat yang ditemukan</p>
                </div>
              ) : (
                filteredJenisSurat.map((jenis) => (
                  <div
                    key={jenis.id}
                    onClick={() => handleSelectJenis(jenis)}
                    className="bg-white rounded-2xl shadow-sm hover:shadow-md active:scale-[0.98] transition-all cursor-pointer overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl flex items-center justify-center shadow-md">
                          <FiFileText className="text-white" size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-800 mb-1 line-clamp-2">
                            {jenis.nama_surat}
                          </h3>
                          {jenis.deskripsi && (
                            <p className="text-sm text-slate-500 line-clamp-2">
                              {jenis.deskripsi}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-xs">
                              <FiClock size={12} />
                              {jenis.fields?.length || 0} field
                            </span>
                            {jenis.auto_generate_nomor && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-600 rounded-md text-xs">
                                <FiCheckCircle size={12} />
                                Auto nomor
                              </span>
                            )}
                            {jenis.require_verification ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-50 text-yellow-700 rounded-md text-xs font-medium border border-yellow-200">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Perlu Verifikasi
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 rounded-md text-xs font-medium border border-green-200">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Tanpa Verifikasi
                              </span>
                            )}
                          </div>
                        </div>
                        <FiChevronRight className="text-slate-400 flex-shrink-0" size={20} />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            // Step 2: Form Input
            <div className="space-y-4">
              {/* Info Card */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-4 border border-blue-100">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FiUser className="text-blue-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900 mb-1">Informasi</h3>
                    <p className="text-sm text-blue-700">
                      Masukkan NIK untuk mengisi data otomatis. Pastikan semua data sudah benar sebelum mengirim.
                    </p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="bg-white rounded-2xl shadow-sm p-4 space-y-4">
                {selectedJenis?.fields
                  ?.filter(field => field.showInDocument !== false || field.type !== 'hidden')
                  .map(renderField)}
              </div>

              {/* Action Buttons */}
              <div className="sticky bottom-20 pb-4">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-xl font-semibold hover:from-slate-800 hover:to-slate-900 active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-md disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Mengirim...
                    </>
                  ) : (
                    <>
                      <FiSend size={20} />
                      Kirim
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        title="Surat Berhasil Diajukan!"
        message={
          selectedJenis?.require_verification
            ? "Surat Anda telah berhasil diajukan dan akan segera diproses oleh RT/RW"
            : "Surat Anda telah berhasil diajukan dan akan langsung diproses oleh Kepala Desa"
        }
        requireVerification={selectedJenis?.require_verification || false}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/warga/dashboard');
        }}
        onContinue={() => {
          setShowSuccessModal(false);
          navigate('/warga/history');
        }}
      />

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </WargaLayout>
  );
};

export default SuratMobile;

